import { useState } from "react";
import { motion } from "framer-motion";
import { useQuizSubmit } from "@/hooks/useQuizSubmit";
import { useWaitlistSubmit } from "@/hooks/useWaitlistSubmit";
import { useIsMobile } from "@/hooks/use-mobile";

const QUESTIONS: { q: string; o: string[] }[] = [
  {
    q: "Where does your closet let you down most?",
    o: ["Work & big meetings", "Weekends & errands", "Dates, dinners & events", "Packing for trips"],
  },
  {
    q: "How many pieces have you worn once — or never?",
    o: ["A couple, max", "One shelf’s worth", "Please don’t make me count", "Zero. I wear everything"],
  },
  {
    q: "When you buy something new, it usually…",
    o: ["Matches nothing I own", "Works with everything", "Was a total impulse", "Replaces something worn out"],
  },
  {
    q: "What would change your mornings the most?",
    o: ["Looking more put-together", "Deciding faster", "Buying less, wearing more", "A full style refresh"],
  },
  {
    q: "It’s 7:40am and nothing feels right. What do you grab?",
    o: [
      "The same trusted outfit as always",
      "Something new — tags still on",
      "Whatever survived laundry day",
      "I try three things and run late",
    ],
  },
];

/** Staggered entrance delays for the four answer buttons (.05/.11/.17/.23s). */
const OPTION_DELAY = ["gfq-d-05", "gfq-d-11", "gfq-d-17", "gfq-d-23"];

// Keyframes + animation utilities ported verbatim from the design reference so the
// motion timings stay exact (Tailwind can't express these without config changes).
const QUIZ_CSS = `
@keyframes gfqQIn { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes gfqPopIn { 0% { opacity: 0; transform: scale(.7) translateY(10px); } 60% { transform: scale(1.05) translateY(-2px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes gfqTagSpring { 0% { opacity: 0; transform: translateY(12px) scale(.8) rotate(-3deg); } 60% { opacity: 1; transform: translateY(-3px) scale(1.06) rotate(1deg); } 100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); } }
@keyframes gfqHoldIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes gfqLineDrawX { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes gfqShakeX { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-2px); } 50% { transform: translateX(2px); } 75% { transform: translateX(-1px); } }
.gfq-qin { animation: gfqQIn .35s ease both; }
.gfq-qin-slow { animation: gfqQIn .4s ease both; }
.gfq-popin { animation: gfqPopIn .45s cubic-bezier(.2,1.2,.4,1) both; }
.gfq-tagspring { animation: gfqTagSpring .5s cubic-bezier(.2,1.2,.4,1) both; }
.gfq-holdin { animation: gfqHoldIn .4s both; }
.gfq-linedraw { animation: gfqLineDrawX .3s ease both; transform-origin: left; }
.gfq-shake { animation: gfqShakeX .3s ease; }
.gfq-d-05 { animation-delay: .05s; }
.gfq-d-11 { animation-delay: .11s; }
.gfq-d-17 { animation-delay: .17s; }
.gfq-d-23 { animation-delay: .23s; }
.gfq-d-1 { animation-delay: .1s; }
.gfq-d-2 { animation-delay: .2s; }
.gfq-d-3 { animation-delay: .3s; }
.gfq-d-4 { animation-delay: .4s; }
.gfq-d-55 { animation-delay: .55s; }
@media (prefers-reduced-motion: reduce) {
  .gfq-qin, .gfq-qin-slow, .gfq-popin, .gfq-tagspring, .gfq-holdin, .gfq-linedraw, .gfq-shake { animation: none; }
}
`;

/**
 * Gap Finder quiz — five light wardrobe questions, then an email gate that unlocks
 * a 10%-off code. Answers are captured alongside the email as lead data.
 */
interface GapFinderQuizProps {
  /**
   * Tags the waitlist signup with the page it came from (e.g. "wharton"), which
   * decides which welcome email is sent. Omit on the main site.
   */
  signupSource?: string;
}

export default function GapFinderQuiz({ signupSource }: GapFinderQuizProps = {}) {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState<number[]>([]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Mobile only: an option tap highlights it and NEXT commits, so a stray tap
  // on a 52px row doesn't advance the quiz. Desktop keeps tap-to-advance.
  const [pendingPick, setPendingPick] = useState<number | null>(null);
  const isMobile = useIsMobile();
  // Stable id so the answers row and the (optional) email land on the same record.
  const [sessionId] = useState(() => crypto.randomUUID());
  const { recordAnswers, submitEmail } = useQuizSubmit();
  const { submit: joinWaitlist } = useWaitlistSubmit();

  const pick = (index: number) => {
    const next = [...picks, index];
    setPicks(next);
    setPendingPick(null);
    setStep((prev) => prev + 1);
    // Quiz finished — capture the answers now, even if they never enter an email.
    if (next.length === QUESTIONS.length) {
      recordAnswers(sessionId, next);
    }
  };

  const unlock = async () => {
    if (email.indexOf("@") < 1) {
      setError(true);
      return;
    }
    setSubmitting(true);
    // Capture the lead + fire the reward email, and add them to the waitlist
    // (Cello is at capacity — there's no instant audit to send them to).
    await submitEmail(sessionId, email, picks);
    try {
      await joinWaitlist({
        email,
        howDidYouHear: "Gap Finder quiz",
        source: signupSource ?? null,
      });
    } catch (err) {
      console.error("Gap Finder waitlist join failed", err);
    }
    setSubmitting(false);
    setUnlocked(true);
  };

  const label = step < 5
    ? `QUESTION 0${step + 1} / 05`
    : unlocked
      ? "YOUR RESULT"
      : "";

  return (
    <section className="relative bg-[#FFF7F0] md:bg-[#2B2724] py-[72px] sm:py-32 border-y border-[#e7ded3] md:border-[#e2d8ce] overflow-hidden">
      <style>{QUIZ_CSS}</style>

      {/* Ambient background video — desktop only; mobile sits on the cream ground. */}
      {!isMobile && (
        <>
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/videos/gap-finder-bg-poster.jpg"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover md:block"
          >
            <source src="/videos/gap-finder-bg.mp4" type="video/mp4" />
          </video>
          {/* Readability scrim so the quiz card stays legible over the footage */}
          <div className="pointer-events-none absolute inset-0 hidden bg-[#1c130c]/55 md:block" aria-hidden="true" />
        </>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="max-w-[1600px] mx-auto relative z-10 px-6 sm:px-8 md:px-16 flex flex-col items-center"
      >
        {/* Quiz card */}
        <div className="w-full max-w-[470px] bg-white border-0 md:border border-[#E6E1D9] rounded-2xl overflow-hidden shadow-[0_16px_40px_-24px_rgba(15,23,42,0.3)] md:shadow-[0_20px_60px_rgba(0,0,0,.35)] font-inter md:font-[Helvetica,Arial,sans-serif] text-[#0f172a] md:text-[#2B2724]">
          <div className="relative h-auto md:h-[426px] px-6 py-7 md:px-7 md:py-6 flex flex-col">
            {/* Top bar: state label + progress segments */}
            <div className="flex items-center gap-[14px]">
              <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8B11] md:font-mono md:text-[10px] md:font-normal md:normal-case md:tracking-[.14em] md:text-[#8A857D]">{label}</div>
              <div className="hidden md:flex gap-1 ml-auto">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="w-[22px] h-1 rounded-full bg-[#EFE9E1] overflow-hidden">
                    {step >= i && <div className="gfq-linedraw w-full h-full rounded-full bg-[#EC6532]" />}
                  </div>
                ))}
              </div>
            </div>

            {/* State 1 — questions. Keyed by step so entrances re-run on every advance. */}
            {step < 5 && (
              <div key={step} className="mt-3.5 md:mt-[26px]">
                <div className="gfq-qin font-playfair text-[26px] font-bold leading-[1.15] md:font-[Helvetica,Arial,sans-serif] md:text-[18px] md:leading-[1.35] md:tracking-[-.01em]">
                  {QUESTIONS[step].q}
                </div>
                <div className="flex flex-col gap-[10px] mt-6 md:mt-5">
                  {QUESTIONS[step].o.map((option, i) => {
                    const chosen = isMobile && pendingPick === i;
                    return (
                      <button
                        key={option}
                        type="button"
                        aria-pressed={isMobile ? chosen : undefined}
                        onClick={() => (isMobile ? setPendingPick(i) : pick(i))}
                        className={`gfq-qin ${OPTION_DELAY[i]} flex w-full items-center justify-between text-left min-h-[52px] md:min-h-0 px-[18px] md:px-4 py-0 md:py-[13px] text-[15px] md:text-[13.5px] font-medium md:font-normal text-[#0f172a] md:text-[#2B2724] rounded-lg md:rounded-[10px] transition-colors hover:border-[#EC6532] hover:bg-[#FFF7F2] ${
                          chosen
                            ? "border-2 border-[#FF8B11] bg-[#FFF1E4]"
                            : "border border-[#e7e5e4] md:border-[#E6E1D9] bg-white md:bg-[#FAF8F5]"
                        }`}
                      >
                        {option}
                        {chosen && <span aria-hidden="true" className="font-bold text-[#FF8B11]">✓</span>}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  aria-disabled={pendingPick === null}
                  onClick={() => { if (pendingPick !== null) pick(pendingPick); }}
                  className={`md:hidden mt-5 h-12 w-full rounded-md text-[11px] font-bold uppercase tracking-[0.15em] text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.25)] active:translate-y-[2px] active:shadow-none ${
                    pendingPick === null ? "bg-[#a8a29e]" : "bg-[#FF8B11]"
                  }`}
                >
                  {step === QUESTIONS.length - 1 ? "See my gaps" : "Next"}
                </button>
              </div>
            )}

            {/* State 2 — email gate */}
            {step >= 5 && !unlocked && (
              <div className="mt-[38px] flex flex-col gap-[14px] items-center text-center">
                <div className="gfq-holdin font-mono text-[26px] font-bold tracking-[.08em] text-[#EC6532] blur-[9px] select-none">
                  CELLO10
                </div>
                <div className="gfq-popin gfq-d-1 font-playfair text-[24px] font-bold leading-[1.15] md:font-[Helvetica,Arial,sans-serif] md:text-[18px] md:leading-normal md:tracking-[-.01em]">
                  Don’t worry — we got you.
                </div>
                <div className="gfq-qin gfq-d-2 text-[12.5px] text-[#6E6961] max-w-[300px] leading-[1.5]">
                  Drop your email and we’ll send you{" "}
                  <span className="text-[#EC6532] font-bold">10% off</span> your styling in your inbox.
                </div>
                <div className="gfq-qin gfq-d-3 flex flex-col md:flex-row gap-2 w-full max-w-[340px]">
                  <label htmlFor="gap-finder-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="gap-finder-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") unlock();
                    }}
                    placeholder="you@example.com"
                    aria-invalid={error}
                    className="flex-1 min-w-0 h-12 md:h-auto px-[14px] py-0 md:py-[11px] bg-white border border-[#d6d3d1] md:border-[#E0DAD1] rounded-md md:rounded-[10px] text-[16px] md:text-[13px] text-[#0f172a] md:text-[#2B2724] outline-none focus:border-[#FF8B11] md:focus:border-[#EC6532]"
                  />
                  <button
                    type="button"
                    onClick={unlock}
                    disabled={submitting}
                    className="h-12 md:h-auto font-inter uppercase tracking-[0.15em] bg-[#FF8B11] shadow-[inset_0_-2px_0_#c2410c] rounded-md md:font-mono md:normal-case md:tracking-[.08em] md:bg-[#EC6532] md:shadow-none md:rounded-[10px] md:hover:bg-[#C74F1F] text-[11px] font-bold disabled:opacity-60 text-white px-4 py-0 md:py-[11px] whitespace-nowrap transition-colors"
                  >
                    {submitting ? "SENDING…" : "UNLOCK + 10% OFF →"}
                  </button>
                </div>
                {error && (
                  <div role="alert" className="gfq-shake font-mono text-[10px] tracking-[.1em] text-[#DC2626]">
                    ENTER A VALID EMAIL
                  </div>
                )}
              </div>
            )}

            {/* State 3 — unlocked result */}
            {step >= 5 && unlocked && (
              <div className="mt-[44px] flex flex-col gap-[14px] items-center text-center">
                <div className="gfq-holdin font-mono text-[10px] tracking-[.16em] text-[#8A857D]">
                  YOU’RE ON THE LIST
                </div>
                <div className="gfq-tagspring gfq-d-1 font-playfair text-[26px] font-bold tracking-[-.01em] text-[#FF8B11] md:font-[Helvetica,Arial,sans-serif] md:text-[#EC6532]">
                  10% off, unlocked
                </div>
                <div className="gfq-tagspring gfq-d-3 flex items-center gap-2 bg-[#FFF7F2] border border-dashed border-[#EC6532] rounded-[10px] px-4 py-[10px]">
                  <span className="font-mono text-[13px] font-bold tracking-[.12em] text-[#EC6532]">
                    CODE: CELLO10
                  </span>
                </div>
                <div className="gfq-qin-slow gfq-d-4 text-[12.5px] text-[#6E6961] max-w-[320px] leading-[1.5]">
                  Save your code — it’s good on your first Cello audit. We’re at capacity right now, so you’re on the waitlist and we’ll email you the moment a spot opens.
                </div>
                <div className="gfq-popin gfq-d-55 mt-0.5 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[.08em] text-[#2B2724] bg-[#FAF8F5] border border-[#E6E1D9] rounded-[10px] px-[18px] py-3">
                  <span className="text-[#EC6532]">✓</span> You’re on the waitlist
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
